import React, { useEffect, useState } from 'react';
import {
  fetchIndicatorsAdoption,
  fetchIndicatorsAdoptionCondominiums,
} from '../../../../helpers/api/finance';
import IndicatorsAdoptionFunnel from './IndicatorsAdoptionFunnel';
import IndicatorsAdoptionTable from './IndicatorsAdoptionTable';

const INITIAL_META = {
  total: 0,
  currentPage: 1,
  lastPage: 1,
  perPage: 10,
  to: null,
  from: null,
};

/**
 * Embudo de adopción: de los condominios registrados, cuántos hicieron la
 * carga inicial, emitieron planilla, tienen propietarios activos y reciben
 * pagos. Debajo, la lista de condominios de la etapa elegida con el contacto
 * del administrador, para saber a quién hay que activar.
 */
const IndicatorsAdoption = ({ condominiumId, startDate, endDate }) => {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);

  const [stage, setStage] = useState(null);
  const [hasRecords, setHasRecords] = useState('without');
  const [textSearch, setTextSearch] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(INITIAL_META);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadSummary = async () => {
      setSummaryLoading(true);
      setSummaryError(false);

      try {
        const response = await fetchIndicatorsAdoption({
          condominium_id: condominiumId || null,
          start_date: startDate,
          end_date: endDate,
        });

        if (isMounted) {
          setSummary(response?.data || null);
        }
      } catch (error) {
        if (isMounted) {
          setSummary(null);
          setSummaryError(true);
        }
      } finally {
        if (isMounted) {
          setSummaryLoading(false);
        }
      }
    };

    loadSummary();

    return () => {
      isMounted = false;
    };
  }, [condominiumId, startDate, endDate]);

  const applyTextSearch = (value = textSearch) => {
    const term = typeof value === "string" ? value.trim() : "";
    setTextSearch(term);
    setSearch(term);
    setPage(1);
  };

  useEffect(() => {
    let isMounted = true;

    const loadRows = async () => {
      setTableLoading(true);
      setTableError(false);

      try {
        const response = await fetchIndicatorsAdoptionCondominiums(page, limit, {
          condominium_id: condominiumId || null,
          start_date: startDate,
          end_date: endDate,
          stage,
          has_records: stage ? hasRecords : null,
          search: search || null,
        });

        if (isMounted) {
          setRows(Array.isArray(response?.data) ? response.data : []);
          setMeta({
            total: response?.meta?.total ?? 0,
            currentPage: response?.meta?.current_page ?? page,
            lastPage: response?.meta?.last_page ?? 1,
            perPage: response?.meta?.per_page ?? limit,
            to: response?.meta?.to ?? null,
            from: response?.meta?.from ?? null,
          });
        }
      } catch (error) {
        if (isMounted) {
          setRows([]);
          setTableError(true);
        }
      } finally {
        if (isMounted) {
          setTableLoading(false);
        }
      }
    };

    loadRows();

    return () => {
      isMounted = false;
    };
  }, [condominiumId, startDate, endDate, stage, hasRecords, search, page, limit]);

  const selectStage = (nextStage) => {
    setStage(nextStage);
    setHasRecords('without');
    setPage(1);
  };

  return (
    <>
      <h5 className="mb-3">Adopción de Homelan</h5>
      <IndicatorsAdoptionFunnel
        summary={summary}
        loading={summaryLoading}
        error={summaryError}
        selectedStage={stage}
        onSelectStage={selectStage}
      />
      <IndicatorsAdoptionTable
        rows={rows}
        meta={meta}
        loading={tableLoading}
        error={tableError}
        stage={stage}
        hasRecords={hasRecords}
        onHasRecordsChange={(value) => {
          setHasRecords(value);
          setPage(1);
        }}
        textSearch={textSearch}
        onTextSearchChange={setTextSearch}
        onSearch={applyTextSearch}
        nextPage={() => setPage((prev) => (prev < meta.lastPage ? prev + 1 : prev))}
        previousPage={() => setPage((prev) => (prev > 1 ? prev - 1 : 1))}
        goToPage={setPage}
        perPage={(value) => {
          setLimit(value);
          setPage(1);
        }}
      />
    </>
  );
};

export default IndicatorsAdoption;
